import { Inject, Injectable } from '@angular/core';
import { BASE_PATH } from 'lemunzo-api';

export type ApiUrlParamValue = string | number | boolean | null | undefined;

@Injectable({
    providedIn: 'root',
})
/**
 * Builds absolute API URLs from Swagger/OpenAPI-generated URL templates.
 *
 * The service prefixes templates with the configured API base path, replaces path placeholders such as `{contractTemplateId}`
 * with encoded values, and optionally appends query parameters. It is intended for cases where a URL string
 * is required directly, for example preview, print, or download links.
 */
export class ApiUrlService {
    constructor(@Inject(BASE_PATH) private readonly basePath: string) {}

    /**
     * Builds an absolute API URL from a URL template.
     *
     * @param template URL template, typically generated from Swagger/OpenAPI,
     * for example `/api/contracttemplate/{contractTemplateId}/data`.
     * @param pathParams Values used to replace path placeholders in the template. Each placeholder
     * must have a matching non-null and non-undefined value.
     * @param queryParams Optional query parameters appended to the generated URL.
     * Array values are appended as repeated query parameters.
     * @returns Absolute API URL prefixed with the configured API base path.
     * @throws Error when a path placeholder has no matching value in `pathParams`.
     */
    build(
        template: string,
        pathParams: Record<string, ApiUrlParamValue> = {},
        queryParams: Record<string, ApiUrlParamValue | ApiUrlParamValue[]> = {}
    ): string {
        const path = template.replace(
            /\{([A-Za-z0-9_.$-]+)}/g,
            (_match, key: string) => {
                const value = pathParams[key];

                if (value === undefined || value === null) {
                    throw new Error(`Missing URL path parameter: ${key}`);
                }

                return encodeURIComponent(String(value));
            }
        );

        return `${this.trimTrailingSlash(this.basePath)}${path}${this.buildQueryString(queryParams)}`;
    }

    private buildQueryString(
        queryParams: Record<string, ApiUrlParamValue | ApiUrlParamValue[]>
    ): string {
        const searchParams = new URLSearchParams();

        for (const [key, value] of Object.entries(queryParams)) {
            if (value === undefined || value === null) {
                continue;
            }

            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item !== undefined && item !== null) {
                        searchParams.append(key, String(item));
                    }
                }

                continue;
            }

            searchParams.set(key, String(value));
        }

        const query = searchParams.toString();

        return query ? `?${query}` : '';
    }

    private trimTrailingSlash(value: string): string {
        return value.endsWith('/') ? value.slice(0, -1) : value;
    }
}
